import { Optional } from '@vimeo/core/types/generic';

import { IntegrationLogger } from '../IntegrationLogger';
import type { TIntegrationConfigUnionPartial } from '../shared/integration';

export abstract class ConfigStorageService<Config extends TIntegrationConfigUnionPartial> {
  public abstract saveConfig(config: Config): void;
  public abstract loadConfig(): Optional<Config>;
  public abstract clearConfig(): void;
}

export class ConfigSessionStorageService<
  Config extends TIntegrationConfigUnionPartial,
> extends ConfigStorageService<Config> {
  private static readonly STORAGE_KEY = 'vimeo-integration-sdk-storage';

  private readonly log: IntegrationLogger = IntegrationLogger.create('ConfigSessionStorageService');

  public saveConfig(config: Config): void {
    try {
      sessionStorage.setItem(ConfigSessionStorageService.STORAGE_KEY, JSON.stringify(config));
      this.log.info('config saved to sessionStorage');
    } catch (error) {
      this.log.warn('failed to save config to sessionStorage', error);
    }
  }

  public loadConfig(): Optional<Config> {
    try {
      const stored = sessionStorage.getItem(ConfigSessionStorageService.STORAGE_KEY);
      if (!stored) {
        this.log.info('no data found in sessionStorage');
        return null;
      }
      const configData = JSON.parse(stored) as Config;
      this.log.info('config loaded from sessionStorage');
      return configData;
    } catch (error) {
      this.log.warn('failed to load config from sessionStorage', error);
      return null;
    }
  }

  public clearConfig(): void {
    try {
      sessionStorage.removeItem(ConfigSessionStorageService.STORAGE_KEY);
      this.log.info('config cleared from sessionStorage');
    } catch (error) {
      this.log.warn('failed to clear config from sessionStorage', error);
    }
  }
}

export class ConfigMemoryStorageService<
  Config extends TIntegrationConfigUnionPartial,
> extends ConfigStorageService<Config> {
  private config: Optional<Config> = null;

  public saveConfig(config: Config): void {
    this.config = config;
  }

  public loadConfig(): Optional<Config> {
    return this.config;
  }

  public clearConfig(): void {
    this.config = null;
  }
}
